import { RegistrationType } from 'app/features/manual-input-registration/models';
import {
    isCodeListValid,
    ScanCodeListDefinition,
    ScannedCode,
    ScannedCodeType,
    scannedCodeTypes,
} from 'app/features/scan-flow/models';
import { ProductBoxRegistration } from 'app/shared/components/product-box/models';
import {
    loginRegistrationDefinition,
    logoutRegistrationDefinition,
    startBreakRegistrationDefinition,
} from 'app/shared/components/system-and-break-activity/models';
import { Activity } from 'app/shared/features/activity-selector/models';
import {
    areProductBoxRegistrationsValid,
    isAmountResultProvided,
    isAmountResultValidOrEmpty,
} from 'app/shared/utils';

import {
    isActivityWithProductBoxRequirement,
    isProductBoxListRequired,
    isSingleAmountResultRequired,
} from '../../../utils/scan-activity.utils';
import {
    filterByCodeType,
    getActivityCode,
    getFirstByCodeType,
} from '../../../utils/scanned-code-list.utils';
import {
    isContainerMeasurementWeight,
    isResultRegistrationActivity,
} from '../scan-flow-result-registration/scan-flow-result-registration.utils';
import { ScanFlowRegistrationData } from '../scan-flow.reducer';

const isScannedCodeMatchWithListDefinition = (
    scanCodeListDefinitions: ScanCodeListDefinition[],
    scannedCodes: ScannedCode[],
    codeType: ScannedCodeType,
) => {
    const listDefinition = getFirstByCodeType(scanCodeListDefinitions, codeType);
    const scannedCodesByCodeType = filterByCodeType(scannedCodes, codeType);

    return (
        (!listDefinition && !scannedCodesByCodeType.length) ||
        (!!listDefinition && isCodeListValid(listDefinition, scannedCodesByCodeType))
    );
};

export const areAllScannedCodeTypeValid = (
    scanCodeListDefinitions: ScanCodeListDefinition[],
    scannedCodes: ScannedCode[],
) => {
    const requiredCodeTypes = ['activity'] as ScannedCodeType[];
    const hasRequiredCodeType = requiredCodeTypes.some(
        (type) => filterByCodeType(scannedCodes, type).length > 0,
    );
    if (!hasRequiredCodeType && scanCodeListDefinitions.length > 0) return false;

    // If activity or system-activity code exists, check all other scanned code types
    // Filter the scanned code types to exclude 'activity'
    const remainingCodeTypes = scannedCodeTypes.filter(
        (codeType: ScannedCodeType) => !requiredCodeTypes.includes(codeType),
    );
    return remainingCodeTypes.every((codeType: ScannedCodeType) =>
        isScannedCodeMatchWithListDefinition(scanCodeListDefinitions, scannedCodes, codeType),
    );
};

export const areSomeScannedCodeTypeValid = (
    scanCodeListDefinitions: ScanCodeListDefinition[],
    scannedCodes: ScannedCode[],
) => {
    if (!scanCodeListDefinitions.length) return false;

    // Check if there are any other scanned code that is not activity or batch
    // Requires at least one scanned code type to exist
    const requiredCodeTypes = ['activity', 'batch'] as ScannedCodeType[];
    const nonRequiredCodeTypes = scannedCodeTypes.filter(
        (codeType: ScannedCodeType) => !requiredCodeTypes.includes(codeType),
    );
    const hasAnyNonRequiredScannedCode = nonRequiredCodeTypes.some(
        (codeType) => !!filterByCodeType(scannedCodes, codeType).length,
    );
    if (!hasAnyNonRequiredScannedCode) return false;

    // If all requirements fulfilled, check if some of the other code types are valid
    return scannedCodeTypes.some((codeType: ScannedCodeType) =>
        isScannedCodeMatchWithListDefinition(scanCodeListDefinitions, scannedCodes, codeType),
    );
};

export const isAmountResultValid = ({
    amountResult,
    activity,
}: {
    amountResult: number | undefined | null;
    activity: Activity | undefined;
}) => {
    const isActivityWithAmountResultRequirement =
        isSingleAmountResultRequired(activity) || isContainerMeasurementWeight(activity);
    return (
        isActivityWithAmountResultRequirement === isAmountResultProvided(amountResult) &&
        isAmountResultValidOrEmpty({ amountResult, activity })
    );
};

export const areProductBoxesValid = ({
    productBoxRegistrations,
    activity,
}: {
    productBoxRegistrations: ProductBoxRegistration[];
    activity: Activity | undefined;
}) => {
    if (activity && isActivityWithProductBoxRequirement(activity)) {
        return (
            productBoxRegistrations.length > 0 &&
            areProductBoxRegistrationsValid(productBoxRegistrations, activity)
        );
    }
    return !productBoxRegistrations.length;
};

/**
 * Determines if the scan flow registration data is in progress based on the provided scan code list definitions and registration data.
 *
 * @param scanCodeListDefinitions - The definitions of the scan code lists belong to the activity.
 * @param registrationData - The current registration data containing scanned codes, employee number, amount result, and product box registrations.
 * @returns A boolean indicating whether the scan flow registration data is complete.
 */
export function isScanFlowRegistrationDataComplete({
    scanCodeListDefinitions,
    registrationData: { scannedCodes, employeeNumber, amountResult, productBoxRegistrations },
}: {
    scanCodeListDefinitions: ScanCodeListDefinition[];
    registrationData: ScanFlowRegistrationData;
}): boolean {
    const activity = getActivityCode(scannedCodes)?.entity;
    return (
        !!employeeNumber &&
        areAllScannedCodeTypeValid(scanCodeListDefinitions, scannedCodes) &&
        isAmountResultValid({ amountResult, activity }) &&
        areProductBoxesValid({ productBoxRegistrations, activity })
    );
}

/** Determines if the scan flow registration data is in progress.
 *
 * In progress means that the user has started entering data but has not yet completed all required fields.
 * @param scanCodeListDefinitions - The definitions of the scan code lists belong to the activity.
 * @param registrationData - The current registration data containing scanned codes, employee number, amount result, and product box registrations.
 * @returns A boolean indicating whether the scan flow registration data is in progress.
 */
export function isScanFlowRegistrationDataInProgress({
    scanCodeListDefinitions,
    registrationData: { scannedCodes, employeeNumber, amountResult, productBoxRegistrations },
}: {
    scanCodeListDefinitions: ScanCodeListDefinition[];
    registrationData: ScanFlowRegistrationData;
}) {
    const activity = getActivityCode(scannedCodes)?.entity;
    const hasAnyProductBoxRegistration = !!productBoxRegistrations.length;
    const hasEmployeeNumber = !!employeeNumber;

    if (!hasEmployeeNumber) return false;

    const hasSomeValidScannedCodes = areSomeScannedCodeTypeValid(scanCodeListDefinitions, scannedCodes);
    const hasAmountResult = isAmountResultProvided(amountResult);
    const hasAnyProductBoxRegistrationWithAmount = productBoxRegistrations.some(({ amount }) =>
        isAmountResultProvided(amount),
    );

    // Only for activity with resultDeterminationType ByContainerAndProductBox, it requires at least location/containers to be filled
    if (isProductBoxListRequired(activity)) {
        return hasSomeValidScannedCodes && hasAnyProductBoxRegistration;
    }

    if (isResultRegistrationActivity(activity)) {
        const hasAnyScannedContainerCode = !!filterByCodeType(scannedCodes, 'container').length;
        // For container measurement activity, it requires at least container code to be scanned
        // Once container is filled, it requires at least amountResult or productBoxRegistrations to be filled to be considered in progress
        return hasAnyScannedContainerCode && (hasAmountResult || hasAnyProductBoxRegistrationWithAmount);
    }
    // By default, as long as any code is scanned or amount filled, it's considered as in progress
    return hasSomeValidScannedCodes || hasAmountResult || hasAnyProductBoxRegistrationWithAmount;
}

export const getRegistrationType = (code: string) => {
    switch (code.toLowerCase()) {
        case 'login':
            return loginRegistrationDefinition.registrationType;
        case 'logout':
            return logoutRegistrationDefinition.registrationType;
        case 'startbreak':
            return startBreakRegistrationDefinition.registrationType;
        case 'endbreak':
            return RegistrationType.EndPause;
        default:
            return RegistrationType.Labour;
    }
};
